import type { DatumEngine } from '@datum-sdk/engine';
import type { DeviceTier, QualityProfile } from '../qualityProfile';

const DEVICE_TIERS: DeviceTier[] = ['low', 'middle', 'high'];

const panel = (bottomPx: number): HTMLDivElement => {
  const el = document.createElement('div');
  Object.assign(el.style, {
    position: 'fixed',
    bottom: `${bottomPx}px`,
    left: '12px',
    zIndex: '99999',
    padding: '4px 10px',
    fontSize: '12px',
    fontFamily: 'monospace',
    color: '#fff',
    background: 'rgba(0,0,0,0.6)',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    whiteSpace: 'nowrap',
  });
  document.body.appendChild(el);
  return el;
};

/** Picking another device tier reloads with `?force-tier`, as legacy's overlay does. */
const tierButton = (tier: DeviceTier, active: boolean): HTMLButtonElement => {
  const button = document.createElement('button');
  button.textContent = tier;
  Object.assign(button.style, {
    padding: '2px 6px',
    border: `1px solid rgba(255,255,255,${active ? '0.6' : '0.3'})`,
    borderRadius: '3px',
    cursor: active ? 'default' : 'pointer',
    fontSize: '11px',
    fontFamily: 'monospace',
    color: '#fff',
    background: active ? 'rgba(255,255,255,0.25)' : 'transparent',
  });
  if (!active) {
    button.addEventListener('click', () => {
      const next = new URL(window.location.href);
      next.searchParams.set('force-tier', tier);
      if (!next.searchParams.has('tier')) next.searchParams.set('tier', '');
      window.location.href = next.toString();
    });
  }
  return button;
};

/**
 * `?tier` on an SDK scene: the device tier, with buttons to force another, and
 * the engine's idle render tier with the frames it drew in the last second. A
 * static scene must reach `paused · 0 draws/s` (perf-budget rule). The frame
 * count replaces legacy's Stats.js panel, which froze on its last value once
 * the loop parked and so read as "still rendering".
 */
export const mountTierOverlay = (
  engine: DatumEngine,
  profile: QualityProfile | undefined,
): (() => void) => {
  const device = panel(12);
  if (profile) {
    const benchmark = profile.benchmarkMs != null ? ` · ${profile.benchmarkMs.toFixed(1)}ms` : '';
    const cv = profile.benchmarkCv != null ? ` · cv=${profile.benchmarkCv.toFixed(2)}` : '';
    device.append(
      `${profile.deviceClass}${benchmark}${cv}`,
      ...DEVICE_TIERS.map((tier) => tierButton(tier, tier === profile.tier)),
    );
  }
  const status = panel(40);
  let drawn = 0;
  let perSecond = 0;
  const show = (): void => {
    status.textContent = `sdk tier=${engine.getRenderTier()} · ${perSecond} draws/s`;
  };
  const stopFrames = engine.subscribeFrameRendered(() => {
    drawn += 1;
  });
  const timer = window.setInterval(() => {
    perSecond = drawn;
    drawn = 0;
    show();
  }, 1000);
  engine.on('engine:render-tier-changed', show);
  show();
  return () => {
    stopFrames();
    clearInterval(timer);
    engine.off('engine:render-tier-changed', show);
    device.remove();
    status.remove();
  };
};
